import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
  useQuery,
} from "@apollo/client";

/**
 * A wrapper around `useQuery` that will not show loading
 * if we return items from the cache (even if we are still
 * using the network query to get a fresh copy of the data
 * i.e. apollo's `cache-and-network` fetch policy)
 * SWR = stale while revalidate - showing stale cache data while
 * sending a revalidate (fetch query)
 */
export function useSwrQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>
) {
  // the query we are wrapping
  const result = useQuery<TData, TVariables>(query, options);

  let isInCache = true;
  if (result.loading) {
    // we are loading, see if we have the data in the cache
    // while we are loading
    try {
      const cacheResult = result.client.readQuery({
        query,
        variables: result.variables,
      });
      isInCache = cacheResult !== null && cacheResult !== undefined;
    } catch (error) {
      // nothing found in the cache
      isInCache = false;
    }
  }
  return {
    ...result,
    // if we don't have data in the cache then show loading
    loading: !isInCache,
    /** If data is from the cache - not the most recent network request */
    usingCache: isInCache && result.loading,
  };
}
